<template>
  <transition name="cookie-slide">
    <div
      v-if="mostrar"
      class="cookie-banner"
      role="dialog"
      aria-live="polite"
      :aria-label="t.titulo"
    >
      <div class="cookie-inner">
        <div class="cookie-icone" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <div class="cookie-texto">
          <div class="cookie-titulo">{{ t.titulo }}</div>
          <p class="cookie-desc">
            {{ t.desc }}
            <a href="/politica-privacidad" target="_blank" rel="noopener">{{ t.link }}</a>
          </p>
        </div>

        <div class="cookie-acoes">
          <button type="button" class="cookie-btn cookie-btn-secondary" @click="recusar">
            {{ t.rejeitar }}
          </button>
          <button type="button" class="cookie-btn cookie-btn-primary" @click="aceitar">
            {{ t.aceitar }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

import { GA_MEASUREMENT_ID } from '@/constants/analytics'

const STORAGE_KEY = 'oligocheck_cookie_consent_v1'

const mostrar = ref(false)
const idioma = ref('es')

const traducoes = {
  es: {
    titulo: 'Respetamos tu privacidad',
    desc: 'Usamos cookies y tecnologías similares para mejorar tu experiencia, analizar el tráfico y personalizar el contenido. Puedes aceptar todas las cookies o rechazarlas. Tu elección se guardará para futuras visitas.',
    link: 'Más información.',
    aceitar: 'Aceptar todas',
    rejeitar: 'Rechazar'
  },
  pt: {
    titulo: 'Respeitamos a sua privacidade',
    desc: 'Utilizamos cookies e tecnologias semelhantes para melhorar a sua experiência, analisar o tráfego e personalizar o conteúdo. Você pode aceitar todos os cookies ou recusá-los. Sua escolha será salva para futuras visitas.',
    link: 'Saiba mais.',
    aceitar: 'Aceitar todos',
    rejeitar: 'Recusar'
  },
  en: {
    titulo: 'We respect your privacy',
    desc: 'We use cookies and similar technologies to improve your experience, analyze traffic and personalize content. You can accept all cookies or reject them. Your choice will be saved for future visits.',
    link: 'Learn more.',
    aceitar: 'Accept all',
    rejeitar: 'Reject'
  }
}

const t = computed(() => traducoes[idioma.value] || traducoes.es)

const detectarIdioma = () => {
  try {
    const navLang = (navigator.language || navigator.userLanguage || 'es').toLowerCase()
    if (navLang.startsWith('pt')) return 'pt'
    if (navLang.startsWith('en')) return 'en'
  } catch (_) { /* noop */ }
  return 'es'
}

const aplicarConsentimento = (aceito) => {
  try {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = !aceito
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: aceito ? 'granted' : 'denied',
        ad_user_data: aceito ? 'granted' : 'denied',
        ad_personalization: aceito ? 'granted' : 'denied',
        analytics_storage: aceito ? 'granted' : 'denied'
      })
    }
  } catch (_) { /* GA pode não estar carregado (bloqueadores) */ }
}

const aceitar = () => {
  try { localStorage.setItem(STORAGE_KEY, 'accepted') } catch (_) {}
  aplicarConsentimento(true)
  mostrar.value = false
}

const recusar = () => {
  try { localStorage.setItem(STORAGE_KEY, 'declined') } catch (_) {}
  aplicarConsentimento(false)
  mostrar.value = false
}

onMounted(() => {
  idioma.value = detectarIdioma()

  let stored = null
  try { stored = localStorage.getItem(STORAGE_KEY) } catch (_) {}

  if (stored === 'accepted') {
    aplicarConsentimento(true)
  } else if (stored === 'declined') {
    aplicarConsentimento(false)
  } else {
    aplicarConsentimento(false)
    setTimeout(() => { mostrar.value = true }, 800)
  }
})
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
  max-width: 1100px;
  margin: 0 auto;
  background: rgba(14, 22, 32, 0.96);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 18px 22px;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.6);
  color: #fff;
  font-family: 'Inter', 'Alexandria', system-ui, -apple-system, sans-serif;
}

.cookie-inner {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 18px;
  align-items: center;
}

.cookie-icone {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(20, 203, 240, 0.18), rgba(20, 203, 240, 0.06));
  border: 1px solid rgba(20, 203, 240, 0.25);
  color: #14cbf0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cookie-texto { min-width: 0; }

.cookie-titulo {
  font-weight: 700;
  font-size: 14px;
  color: #fff;
  margin-bottom: 4px;
  letter-spacing: -0.1px;
}

.cookie-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  line-height: 1.55;
}

.cookie-desc a {
  color: #1aaf95;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;
}

.cookie-desc a:hover { color: #14cbf0; }

.cookie-acoes {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.cookie-btn {
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  letter-spacing: 0.1px;
  font-family: inherit;
}

.cookie-btn-secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}

.cookie-btn-secondary:hover {
  background: rgba(255, 255, 255, 0.09);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.18);
}

.cookie-btn-primary {
  background: linear-gradient(135deg, #14cbf0, #1aaf95);
  color: #071018;
  box-shadow: 0 6px 18px -4px rgba(20, 203, 240, 0.5);
}

.cookie-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -4px rgba(20, 203, 240, 0.65);
}

.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1), opacity 0.3s ease;
}
.cookie-slide-enter-from,
.cookie-slide-leave-to {
  transform: translateY(120%);
  opacity: 0;
}

@media (max-width: 780px) {
  .cookie-banner {
    left: 10px;
    right: 10px;
    bottom: 10px;
    padding: 16px 16px 14px;
    border-radius: 16px;
  }
  .cookie-inner {
    grid-template-columns: 40px 1fr;
    grid-template-rows: auto auto;
    gap: 12px 14px;
  }
  .cookie-acoes {
    grid-column: 1 / -1;
    width: 100%;
    justify-content: stretch;
  }
  .cookie-btn {
    flex: 1;
    padding: 11px 14px;
    text-align: center;
  }
  .cookie-titulo { font-size: 13.5px; }
  .cookie-desc { font-size: 12.5px; }
}
</style>
